import type { CouleurAPI } from "@/types/mel-components";

/** Ordre sidebar UI eFashion v4 */
export const MOTIF_CATEGORIES = [
  "Animaux",
  "Camouflage",
  "Carreaux",
  "Ethnique",
  "Figuratif",
  "Géométrique",
  "Motifs",
  "Pois",
  "Typographique",
  "Végétation",
  "Vintage",
] as const;

export type MotifCategory = (typeof MOTIF_CATEGORIES)[number];

export const MOTIF_CATEGORY_SWATCH = "#808080";

export interface MotifCategoryItem {
  id: MotifCategory;
  label: MotifCategory;
}

export function isMotifColor(
  color: Pick<CouleurAPI, "motifCategorie">,
): boolean {
  return Boolean(color.motifCategorie?.trim());
}

export function splitSolidAndMotifColors<T extends Pick<CouleurAPI, "motifCategorie">>(
  colors: T[],
): { solid: T[]; motifs: T[] } {
  const solid: T[] = [];
  const motifs: T[] = [];
  for (const color of colors) {
    if (isMotifColor(color)) motifs.push(color);
    else solid.push(color);
  }
  return { solid, motifs };
}

export function groupMotifsByCategory<T extends CouleurAPI>(
  motifs: T[],
): Partial<Record<MotifCategory, T[]>> {
  const groups: Partial<Record<MotifCategory, T[]>> = {};

  for (const color of motifs) {
    const category = color.motifCategorie as MotifCategory | undefined;
    if (!category) continue;
    if (!groups[category]) groups[category] = [];
    groups[category]!.push(color);
  }

  for (const category of Object.keys(groups) as MotifCategory[]) {
    groups[category]?.sort((a, b) =>
      (a.couleur_FR || "").localeCompare(b.couleur_FR || "", "fr"),
    );
  }

  return groups;
}

export function getMotifCategoriesWithColors(
  motifs: CouleurAPI[],
): MotifCategoryItem[] {
  const groups = groupMotifsByCategory(motifs);
  return MOTIF_CATEGORIES.filter((category) => (groups[category]?.length ?? 0) > 0).map(
    (category) => ({ id: category, label: category }),
  );
}

export function formatMotifPanelTitle(category: MotifCategory): string {
  if (category === "Motifs") return "Motifs";
  return `Motifs ${category.toLowerCase()}`;
}
